import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useLimits } from '@/hooks/useLimits';
import { isValidUUID } from '@/lib/utils';
import type { ChecklistTemplate, ChecklistWithProgress } from '../types';

export function useChecklists() {
  const { currentPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();
  const { getNumberLimit, isUnlimited, isLimitEnabled } = useLimits();

  // In baby mode, use lastCompletedPregnancy; otherwise use currentPregnancy
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const rawPregnancyId = currentPregnancy?.id || (isBabyMode ? lastCompletedPregnancy?.id : null);

  // Validate that the pregnancy ID is a valid UUID before using it
  const activePregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  const [checklists, setChecklists] = useState<ChecklistWithProgress[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get limit values
  const checklistLimit = getNumberLimit('checklists_limit');
  const hasUnlimitedChecklists = isUnlimited('checklists_limit');
  const hasAllTemplatesAccess = isLimitEnabled('all_checklist_templates');

  // Count custom checklists
  const customChecklistCount = checklists.filter(c => c.is_custom).length;
  const isChecklistLimitReached = !hasUnlimitedChecklists && customChecklistCount >= checklistLimit;
  const remainingChecklists = hasUnlimitedChecklists ? Infinity : Math.max(0, checklistLimit - customChecklistCount);

  // Fetch user's checklists with progress
  const fetchChecklists = useCallback(async () => {
    if (!activePregnancyId) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch user checklists
      const { data: userChecklists, error: checklistsError } = await supabase
        .from('user_checklists')
        .select('*')
        .eq('pregnancy_id', activePregnancyId)
        .order('created_at', { ascending: true });

      if (checklistsError) throw checklistsError;

      // Fetch items for each checklist with progress calculation
      const checklistsWithProgress: ChecklistWithProgress[] = await Promise.all(
        (userChecklists || []).map(async (checklist) => {
          const { data: items, error: itemsError } = await supabase
            .from('user_checklist_items')
            .select('*')
            .eq('checklist_id', checklist.id)
            .order('order_index', { ascending: true });

          if (itemsError) throw itemsError;

          const total = items?.length || 0;
          const completed = items?.filter(item => item.status === 'completed').length || 0;

          return {
            ...checklist,
            items: items || [],
            progress: {
              completed,
              total,
              percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
            },
          };
        })
      );

      setChecklists(checklistsWithProgress);
    } catch (err) {
      console.error('Error fetching checklists:', err);
      setError('Fehler beim Laden der Checklisten');
    } finally {
      setIsLoading(false);
    }
  }, [activePregnancyId]);

  // Fetch available templates based on user tier, limits, and app mode
  const fetchTemplates = useCallback(async () => {
    try {
      // Build query
      let query = supabase
        .from('checklist_templates')
        .select(`
          *,
          checklist_template_items(count)
        `)
        .eq('is_active', true);

      // If user doesn't have access to all templates, filter by visible_to_free
      if (!hasAllTemplatesAccess) {
        query = query.eq('visible_to_free', true);
      }

      // Filter by target_mode based on app mode (pregnancy vs baby)
      // Show templates that match the current mode OR templates marked as 'both'
      if (isBabyMode) {
        query = query.in('target_mode', ['baby', 'both']);
      } else {
        query = query.in('target_mode', ['pregnancy', 'both']);
      }

      const { data, error: templatesError } = await query.order('order_index', { ascending: true });

      if (templatesError) throw templatesError;

      // Transform to include item count
      const templatesWithCount = (data || []).map(template => ({
        ...template,
        item_count: template.checklist_template_items?.[0]?.count || 0,
      }));

      setTemplates(templatesWithCount);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, [hasAllTemplatesAccess, isBabyMode]);

  // Add template to user's checklists
  const addTemplateChecklist = useCallback(async (templateId: string) => {
    if (!activePregnancyId) return null;

    try {
      // Get template details
      const { data: template, error: templateError } = await supabase
        .from('checklist_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (templateError) throw templateError;

      // Create user checklist
      const { data: newChecklist, error: checklistError } = await supabase
        .from('user_checklists')
        .insert({
          pregnancy_id: activePregnancyId,
          template_id: templateId,
          title: template.title,
          is_custom: false,
          context_type: isBabyMode ? 'baby' : 'pregnancy',
        })
        .select()
        .single();

      if (checklistError) throw checklistError;

      // Get template items
      const { data: templateItems, error: itemsError } = await supabase
        .from('checklist_template_items')
        .select('*')
        .eq('template_id', templateId)
        .order('order_index', { ascending: true });

      if (itemsError) throw itemsError;

      // Create user items from template - only include columns that exist in the table
      if (templateItems && templateItems.length > 0) {
        const userItems = templateItems.map((item, index) => ({
          checklist_id: newChecklist.id,
          title: item.title,
          status: 'open',
          order_index: item.order_index ?? index,
        }));

        console.log('Inserting', userItems.length, 'items for checklist:', newChecklist.id);

        const { error: insertError, data: insertedData } = await supabase
          .from('user_checklist_items')
          .insert(userItems)
          .select();

        if (insertError) {
          console.error('Error inserting checklist items:', insertError.message, insertError.details, insertError.hint);
          throw insertError;
        }

        console.log('Successfully inserted', insertedData?.length, 'items');
      } else {
        console.log('No template items found for template:', templateId);
      }

      await fetchChecklists();
      return newChecklist;
    } catch (err) {
      console.error('Error adding template checklist:', err);
      setError('Fehler beim Hinzufügen der Checkliste');
      return null;
    }
  }, [activePregnancyId, isBabyMode, fetchChecklists]);

  // Create custom checklist
  const createCustomChecklist = useCallback(async (title: string) => {
    if (!activePregnancyId) return null;

    // Check limit
    if (isChecklistLimitReached) {
      setError(`Du hast das Limit von ${checklistLimit} eigenen Checklisten erreicht.`);
      return null;
    }

    try {
      const { data: newChecklist, error: checklistError } = await supabase
        .from('user_checklists')
        .insert({
          pregnancy_id: activePregnancyId,
          title,
          is_custom: true,
          context_type: isBabyMode ? 'baby' : 'pregnancy',
        })
        .select()
        .single();

      if (checklistError) throw checklistError;

      await fetchChecklists();
      return newChecklist;
    } catch (err) {
      console.error('Error creating custom checklist:', err);
      setError('Fehler beim Erstellen der Checkliste');
      return null;
    }
  }, [activePregnancyId, isBabyMode, fetchChecklists, isChecklistLimitReached, checklistLimit]);

  // Delete checklist
  const deleteChecklist = useCallback(async (checklistId: string) => {
    try {
      // Items are deleted via cascade
      const { error: deleteError } = await supabase
        .from('user_checklists')
        .delete()
        .eq('id', checklistId);

      if (deleteError) throw deleteError;

      await fetchChecklists();
      return true;
    } catch (err) {
      console.error('Error deleting checklist:', err);
      setError('Fehler beim Löschen der Checkliste');
      return false;
    }
  }, [fetchChecklists]);

  // Initial fetch
  useEffect(() => {
    fetchChecklists();
    fetchTemplates();
  }, [fetchChecklists, fetchTemplates]);

  // Setup realtime subscription for both checklists and items
  useEffect(() => {
    if (!activePregnancyId) return;

    const channel = supabase
      .channel('checklist-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_checklists',
        },
        () => {
          fetchChecklists();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_checklist_items',
        },
        () => {
          fetchChecklists();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activePregnancyId, fetchChecklists]);

  return {
    checklists,
    templates,
    isLoading,
    error,
    refetch: fetchChecklists,
    addTemplateChecklist,
    createCustomChecklist,
    deleteChecklist,
    // Limit info
    customChecklistCount,
    checklistLimit,
    hasUnlimitedChecklists,
    isChecklistLimitReached,
    remainingChecklists,
  };
}
